import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { getMissionPage, getMissionPageIfUid, type MissionPage } from '@/lib/firebase/mission';
import { getUserData, type UserData, resolveUserIdFromIdentifier } from '@/lib/firebase/user';
import { getUserPosts } from '@/lib/firebase/posts';
import { Post } from '@/lib/firebase/posts';
// Membership tiers removed
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorMessage from '@/components/ui/ErrorMessage';
import MissionHeader from '@/components/mission/MissionHeader';
import MissionTabs from '@/components/mission/MissionTabs';
import { useTranslation } from 'react-i18next';
import MissionContent from '@/components/mission/MissionContent';
import Footer from '@/components/homepage/Footer';
import PublicAboutContent from '@/components/about/PublicAboutContent';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { trackDonationCompleted, trackDonationFeedback, trackPageLoadTimedOut } from '@/lib/utils/analytics';
import { Dialog } from '@headlessui/react';
import { CheckCircle2, AlertCircle, Heart } from 'lucide-react';
import Button from '@/components/ui/Button';
import { usePageMeta } from '@/hooks/usePageMeta';
import { stripHtml } from '@/lib/utils';
import { sports } from '@/lib/data/denominations';

const baseTabs = (t: (k: string) => string) => ([
  { name: t('creator.mypage.tabs.home'), href: '#home' },
  { name: t('creator.mypage.tabs.about'), href: '#about' },
]);

export default function MissionPage() {
  const { t } = useTranslation();
  const params = useParams();
  const rawIdentifier = (params as any).creatorId || '';
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(null);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mission, setMission] = useState<MissionPage | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  // Membership tiers removed
  const [isLoading, setIsLoading] = useState(true);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('#home');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [allowFreeFollowing, setAllowFreeFollowing] = useState(false);
  const [hasTriedResolve, setHasTriedResolve] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  // Set only by the fast UID path below, which already fetched the full
  // missionpages/{uid} doc -- lets loadData() skip re-fetching it.
  const [prefetchedMission, setPrefetchedMission] = useState<MissionPage | undefined>(undefined);
  const [donationFeedbackGiven, setDonationFeedbackGiven] = useState(false);
  const [creatorStripeStatus, setCreatorStripeStatus] = useState<{
    hasStripeAccount: boolean;
    isVerified: boolean;
  } | null>(null);

  const isPreview = location.search.includes('preview=true');

  const missionDisplayName = userData?.displayName || mission?.title;
  const pageUrl = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : undefined;
  const sportLabel = userData?.sport
    ? sports.find(d => d.value === userData.sport)?.label || userData.sport
    : undefined;
  // Structured-data fact strip (sport / club / next goal): the About tab already
  // shows these to human visitors (PublicAboutContent.tsx); mirroring them as
  // schema.org additionalProperty lets crawlers/AI answer engines surface them
  // without a visitor having to click into the About tab first.
  const factStripProperties = [
    sportLabel ? { '@type': 'PropertyValue', name: 'Sport', value: sportLabel } : undefined,
    userData?.club ? { '@type': 'PropertyValue', name: 'Club', value: userData.club } : undefined,
    userData?.nextGoal ? { '@type': 'PropertyValue', name: 'Next goal', value: userData.nextGoal } : undefined,
  ].filter(Boolean);
  // TSF-266: description is rich text; raw <p> tags leaked into every share
  // preview ("Support X on Sport Fund — <p>Destiny is..."). Tags -> spaces.
  const plainDescription = mission?.description
    ? stripHtml(mission.description.replace(/></g, '> <')).replace(/\s+/g, ' ').trim()
    : '';
  usePageMeta(
    missionDisplayName ? `${missionDisplayName}'s Fund` : undefined,
    missionDisplayName
      ? `Support ${missionDisplayName} on Sport Fund${plainDescription ? ` — ${plainDescription}` : '.'}`
      : undefined,
    {
      image: mission?.headerImage || undefined,
      // TSF: Person + DonateAction structured data so shared fund-page links can
      // surface as rich results (docs/13-Improvement-Backlog.md "Structured data
      // on /fund/:creatorId"). Only emitted for published pages -- owner previews
      // of a draft shouldn't tell crawlers a donation target exists yet.
      jsonLd:
        missionDisplayName && mission?.isPublished
          ? {
              '@context': 'https://schema.org',
              '@type': 'Person',
              name: missionDisplayName,
              ...(mission?.headerImage ? { image: mission.headerImage } : {}),
              ...(plainDescription ? { description: plainDescription } : {}),
              ...(pageUrl ? { url: pageUrl } : {}),
              ...(factStripProperties.length > 0 ? { additionalProperty: factStripProperties } : {}),
              potentialAction: {
                '@type': 'DonateAction',
                name: `Support ${missionDisplayName}`,
                ...(pageUrl
                  ? {
                      target: {
                        '@type': 'EntryPoint',
                        urlTemplate: pageUrl,
                        actionPlatform: [
                          'https://schema.org/DesktopWebPlatform',
                          'https://schema.org/MobileWebPlatform',
                        ],
                      },
                    }
                  : {}),
              },
            }
          : undefined,
    }
  );

  useEffect(() => {
    // Resolve UID from path or confirm UID
    async function resolve() {
      setPrefetchedMission(undefined);
      if (!rawIdentifier) {
        setResolvedUserId(null);
        setHasTriedResolve(true);
        return;
      }

      const fast = await getMissionPageIfUid(rawIdentifier);
      if (fast) {
        setPrefetchedMission(fast.missionPage);
        setResolvedUserId(fast.uid);
        setHasTriedResolve(true);
        return;
      }

      let uid = await resolveUserIdFromIdentifier(rawIdentifier);

      // Fallback: if resolution failed but current user path matches the identifier, use their UID
      if (!uid && user?.uid) {
        try {
          const currentUser = await getUserData(user.uid);
          if (currentUser?.path === rawIdentifier) {
            uid = user.uid;
          }
        } catch (_) { /* ignore */ }
      }

      // Final fallback: if still unresolved but user is authenticated, assume owner
      if (!uid && user?.uid) {
        uid = user.uid;
      }

      setResolvedUserId(uid);
      setHasTriedResolve(true);
    }
    resolve();
  }, [rawIdentifier, user?.uid]);

  useEffect(() => {
    // Same cancellation guard as the resolve() effect above: keyed on resolvedUserId, but
    // the route can hand it a new value without unmounting, so a slow query for the OLD
    // creator must not set state (or navigate) after a newer run has already started.
    let cancelled = false;
    async function checkSubscription() {
      if (!user?.uid || !resolvedUserId || isPreview) return;

      // Mission owners should see the public page, not be redirected to dashboard
      const isOwner = user.uid === resolvedUserId;
      if (isOwner) {
        if (!cancelled) setIsSubscribed(false);
        return;
      }

      try {
        // 1) Check active subscriptions (monthly or free followers stored in subscriptions)
        const q = query(
          collection(db, 'subscriptions'),
          where('userId', '==', user.uid),
          where('creatorId', '==', resolvedUserId),
          where('status', '==', 'active')
        );

        const snapshot = await getDocs(q);
        if (cancelled) return;
        const isUserSubscribed = !snapshot.empty;
        setIsSubscribed(isUserSubscribed);

        // If user is authenticated and subscribed, redirect to dashboard view of this mission
        if (isUserSubscribed && !location.pathname.startsWith('/dashboard/')) {
          navigate(`/dashboard/mission/${rawIdentifier}`);
          return;
        }

        // 2) Also treat successful one-off donors and active monthly donors as supporters
        // Query only by userId to avoid requiring composite indexes, then filter locally
        const qDonations = query(
          collection(db, 'donations'),
          where('userId', '==', user.uid)
        );
        const donationSnap = await getDocs(qDonations);
        if (cancelled) return;
        const isDonor = donationSnap.docs.some(d => {
          const data = d.data() as any;
          // Grant access for: succeeded one-off donations, active monthly subscriptions, or processing payments
          return data.creatorId === resolvedUserId &&
            (data.status === 'succeeded' || data.status === 'active' || data.status === 'processing');
        });

        if (isDonor) {
          // Mark as subscribed for UI (enables donations tab) and redirect
          setIsSubscribed(true);
          if (!location.pathname.startsWith('/dashboard/')) {
            navigate(`/dashboard/mission/${rawIdentifier}`);
          }
          return;
        }

        // If coming from a successful checkout, grant immediate access and create follow
        const fromSupported = new URLSearchParams(location.search).get('supported') === '1';
        if (fromSupported) {
          setIsSubscribed(true);
          setShowThankYou(true);
          trackDonationCompleted(resolvedUserId);
          if (!location.pathname.startsWith('/dashboard/')) {
            navigate(`/dashboard/mission/${rawIdentifier}?supported=1`);
          }
          return;
        }
      } catch (error) {
        console.error('Error checking supporter status:', error);
      }
    }

    checkSubscription();
    return () => { cancelled = true; };
  }, [user?.uid, resolvedUserId, navigate, isPreview, location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      // Wait for identifier resolution if we have a raw identifier
      if (!rawIdentifier) {
        setError('Fund page not found');
        setIsLoading(false);
        return;
      }
      if (!hasTriedResolve) {
        // Still resolving; keep loading without setting error
        return;
      }
      if (!resolvedUserId) {
        // Still resolving; keep loading without setting error
        setError('Fund page not found');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        let missionData = prefetchedMission !== undefined ? prefetchedMission : await getMissionPage(resolvedUserId);
        if (cancelled) return;

        // Check creator's Stripe status
        const isOwner = user?.uid === resolvedUserId;
        const ownerFallbackUser = isOwner ? await getUserData(resolvedUserId) : null;
        if (cancelled) return;

        // If mission doc is missing but viewer is the owner, create a placeholder so preview works
        if (!missionData && isOwner) {
          const displayName =
            ownerFallbackUser?.displayName ||
            `${ownerFallbackUser?.firstName || ''} ${ownerFallbackUser?.lastName || ''}`.trim() ||
            'My Fund Page';

          missionData = {
            id: resolvedUserId,
            userId: resolvedUserId,
            headerImage: ownerFallbackUser?.coverImage || '',
            title: displayName,
            description: ownerFallbackUser?.biography || '',
            donationImpact: (ownerFallbackUser as any)?.donationImpact || '',
            isPublished: false,
            createdAt: null,
            updatedAt: null,
          } as MissionPage;
        }

        if (!missionData) {
          setError('Fund page not found');
          setIsLoading(false);
          return;
        }

        try {
          const creatorDoc = await getDoc(doc(db, 'users', resolvedUserId));
          if (cancelled) return;
          if (creatorDoc.exists()) {
            const creatorData = creatorDoc.data();
            const stripeStatus = {
              hasStripeAccount: Boolean(creatorData.stripeAccountId),
              isVerified: creatorData.stripeAccountStatus === 'verified'
            };
            setCreatorStripeStatus(stripeStatus);
          }
        } catch (err) {
          console.warn('[MissionPage] Could not read creator Stripe status', err);
        }

        // If page is unpublished, only the owner (or explicit preview) may view it
        if (!missionData.isPublished && !isOwner && !isPreview) {
          setError('Fund page is not published');
          setIsLoading(false);
          return;
        }
        setMission(missionData);

        const [userDataResult, userPosts] = await Promise.all([
          (async () => {
            try {
              return await getUserData(resolvedUserId);
            } catch (err) {
              console.warn('[MissionPage] Could not read users doc, falling back to mission data');
              return null;
            }
          })(),
          getUserPosts(resolvedUserId)
        ]);
        if (cancelled) return;

        // Try to read creator settings but do not fail if blocked
        try {
          const creatorDoc = await getDoc(doc(db, 'users', resolvedUserId));
          if (cancelled) return;
          if (creatorDoc.exists()) {
            setAllowFreeFollowing(creatorDoc.data().allowFreeFollowing || false);
          }
        } catch (_) { /* ignore */ }

        // Fallback user data from mission page if users doc is not readable
        // (TSF-238: users/{uid} is `allow get: if isSignedIn()`, so a
        // logged-out donor never gets here via userDataResult -- photoURL and
        // description are mirrored onto missionpages precisely so this
        // fallback has real values instead of blanks).
        const effectiveUserData: UserData = userDataResult || {
          displayName: missionData.title,
          photoURL: (missionData as any).photoURL || '',
          social: undefined,
          biography: missionData.description,
          // Without this the donate blurb fell through to defaultImpactText, so
          // a logged-out donor read generic boilerplate instead of the
          // creator's own words -- the one field of TSF-238 that survived the
          // backfill, because the fallback object simply did not carry it.
          donationImpact: (missionData as any).donationImpact || '',
        } as any;
        setUserData(effectiveUserData);

        // If mission page header is empty but user doc has a cover image, use it
        if (!missionData.headerImage && (userDataResult as any)?.coverImage) {
          setMission(prev => prev ? { ...prev, headerImage: (userDataResult as any).coverImage } : prev);
        }
        // Only show published posts to non-owner viewers
        const visiblePosts = (userPosts as Post[]).filter(p => (user?.uid === resolvedUserId) ? true : (p.isPublished ?? false));
        setPosts(visiblePosts);
      } catch (error) {
        console.error('[MissionPage] Error loading data:', error);
        setError('Failed to load fund page');
      } finally {
        setIsLoading(false);
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, [resolvedUserId, hasTriedResolve, isPreview, rawIdentifier, prefetchedMission]);

  // Prerender snapshots this route once loading settles (see main.tsx) instead
  // of the generic post-mount timer, so the OG tags usePageMeta sets above
  // reflect this fund's real title/image rather than the homepage defaults.
  useEffect(() => {
    if (!isLoading) {
      document.dispatchEvent(new Event('render-event'));
    }
  }, [isLoading]);

  // Firestore's getDoc calls can hang rather than reject when the client
  // config is bad (confirmed live 2026-08-14: a real visitor to a published
  // fund page saw an indefinite blank spinner, not an error, because the
  // read never settled either way -- see TSF-89/TSF-86). Rather than trust
  // every read to eventually reject, give the user a way out after a fixed
  // wait instead of leaving them on a spinner forever.
  useEffect(() => {
    if (!isLoading) {
      setLoadTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setLoadTimedOut(true);
      trackPageLoadTimedOut('mission_page', resolvedUserId ?? rawIdentifier);
    }, 12000);
    return () => window.clearTimeout(timer);
  }, [isLoading, resolvedUserId, rawIdentifier]);

  // Confetti animation when showing thank-you
  useEffect(() => {
    if (!showThankYou) return;
    try {
      const styleId = 'df-confetti-style';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          .df-confetti { position: fixed; top: -10px; width: 8px; height: 14px; opacity: .9; pointer-events: none; z-index: 9999; }
          @keyframes df-fall { to { transform: translateY(110vh) rotate(720deg); } }
        `;
        document.head.appendChild(style);
      }
      const colors = ['#16a34a', '#22c55e', '#86efac', '#34d399', '#10b981', '#059669'];
      const count = 150;
      const elements: HTMLElement[] = [];
      for (let i = 0; i < count; i++) {
        const el = document.createElement('div');
        el.className = 'df-confetti';
        el.style.left = Math.random() * 100 + 'vw';
        el.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        el.style.transform = `translateY(0) rotate(${Math.random() * 360}deg)`;
        el.style.animation = `df-fall ${1.5 + Math.random()}s linear ${Math.random()}s forwards`;
        document.body.appendChild(el);
        elements.push(el);
      }
      window.setTimeout(() => { elements.forEach(el => el.remove()); }, 3200);
    } catch { /* no-op */ }
  }, [showThankYou]);

  if (isLoading) {
    if (loadTimedOut) {
      return (
        <ErrorMessage message="This fund page is taking longer than usual to load. Please refresh the page — if this keeps happening, let us know at support@thesportfund.com." />
      );
    }
    return <LoadingSpinner />;
  }

  if (error || !mission || !resolvedUserId) {
    return <ErrorMessage message={error || 'Fund page not found'} />;
  }

  if (!userData) {
    return <ErrorMessage message="Fund page not found" />;
  }

  const isOwner = user?.uid === resolvedUserId;

  // Handle unpublished page scenarios
  if (!mission.isPublished && !isPreview) {
    if (!isOwner) {
      // Anonymous user viewing unpublished page
      return (
        <div className="min-h-screen bg-white flex flex-col">
          <div className="flex-1 flex items-center justify-center px-4">
            <div className="max-w-md text-center">
              <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                Page Not Available
              </h1>
              <p className="text-gray-600">
                This profile page is currently under construction and not yet published. Please come back later.
              </p>
            </div>
          </div>
          <Footer />
        </div>
      );
    }

    // TSF-176: the owner of an unpublished page used to be shown a dead end --
    // "please set up your payments ... then publish it" -- if they had not
    // started Stripe. Publishing no longer needs Stripe, so the owner always
    // falls through to their own page with the "not yet published" banner.
  }

  const currentTabs = [
    ...baseTabs(t),
    ...(isSubscribed ? [{ name: t('creator.mypage.myDonations', { defaultValue: 'My donations' }), href: '#donations' }] : [])
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Show simple banner for owner viewing unpublished page */}
      {!mission.isPublished && isOwner && !isPreview && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-900">
                This page is not yet published and only visible to you
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 pb-20 sm:pb-0">
        <MissionHeader
          headerImage={mission.headerImage}
          photoURL={userData?.photoURL || ''}
          title={mission.title}
          displayName={userData?.displayName || mission.title}
          postCount={posts.length}
          social={userData.social}
          creatorId={resolvedUserId}
          creatorPath={rawIdentifier}
          allowFreeFollowing={allowFreeFollowing}
          onDonateClick={() => {
            // MissionContent -- the only 'open-donate' listener -- isn't
            // mounted on the #about tab. Switch to #home first so the
            // listener is attached before the event fires.
            if (activeTab === '#about') {
              setActiveTab('#home');
              setTimeout(() => window.dispatchEvent(new CustomEvent('open-donate', { detail: { surface: 'header' } })), 0);
            } else {
              window.dispatchEvent(new CustomEvent('open-donate', { detail: { surface: 'header' } }));
            }
          }}
        />

        <MissionTabs
          tabs={currentTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        <div className="max-w-5xl mx-auto px-4 pt-4 pb-8">
          {activeTab === '#about' ? (
            <PublicAboutContent
              userData={userData}
              missionDescription={mission.description}
            />
          ) : (
            <MissionContent
              posts={posts}
              membershipTiers={[]}
              userData={userData}
              activeTab={activeTab}
              isSubscribed={isSubscribed}
              creatorId={resolvedUserId}
              creatorPath={rawIdentifier}
              givingEnabled={(mission as any)?.givingEnabled}
              creatorName={userData?.displayName || mission.title}
              creatorPhotoURL={userData?.photoURL || ''}
            />
          )}
        </div>
      </div>

      {/* TSF: mobile-only sticky donate CTA. The header's "Support now" button
          scrolls out of view once a visitor reads past the hero, and real
          traffic showed donate_ask_viewed firing with zero donate_dialog_opened
          for 30 real hours after the header CTA shipped -- this reuses the same
          'open-donate' event so the dialog, its tracking and the abandon-survey
          logic in MissionContent all stay the single source of truth. */}
      {!isOwner && (
        <div
          className="sm:hidden fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <Button
            size="lg"
            className="w-full flex items-center justify-center gap-2"
            onClick={() => {
              if (activeTab === '#about') {
                setActiveTab('#home');
                setTimeout(() => window.dispatchEvent(new CustomEvent('open-donate', { detail: { surface: 'sticky' } })), 0);
              } else {
                window.dispatchEvent(new CustomEvent('open-donate', { detail: { surface: 'sticky' } }));
              }
            }}
          >
            <Heart className="h-4 w-4" />
            Support now
          </Button>
        </div>
      )}
      <Footer />

      {/* Thank you modal after successful donation */}
      <Dialog open={showThankYou} onClose={() => setShowThankYou(false)} className="relative z-[60]">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              <Dialog.Title className="text-lg font-semibold text-gray-900">Thank you for your support</Dialog.Title>
            </div>
            <p className="mt-3 text-sm text-gray-700">We've received your donation and informed the athlete about your support. Thank you for backing their journey.</p>
            {!donationFeedbackGiven ? (
              <div className="mt-4 rounded-lg bg-gray-50 p-3">
                <p className="text-sm text-gray-700">One quick question — how easy was it to support today?</p>
                <div className="mt-2 flex gap-2">
                  {([
                    { rating: 'easy' as const, emoji: '😊', label: 'Easy' },
                    { rating: 'ok' as const, emoji: '😐', label: 'OK' },
                    { rating: 'hard' as const, emoji: '😞', label: 'Hard' },
                  ]).map(({ rating, emoji, label }) => (
                    <button
                      key={rating}
                      type="button"
                      className="flex-1 flex flex-col items-center gap-1 rounded-md border border-gray-200 bg-white py-2 text-xs text-gray-600 hover:border-primary hover:text-primary"
                      onClick={() => {
                        trackDonationFeedback(rating, resolvedUserId || '');
                        setDonationFeedbackGiven(true);
                      }}
                    >
                      <span className="text-lg" aria-hidden="true">{emoji}</span>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-gray-500">Thanks for the feedback!</p>
            )}
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 rounded-md bg-primary text-white hover:bg-primary/90"
                onClick={() => setShowThankYou(false)}
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}